import { type FC, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useHorizontalScroll } from '@/common/hooks/horizontal-hook';
import { TabsContext } from '@/components/Tabs/Tabs';
import { Picture } from '@/components/ui/Picture/Picture';

import { type ShopTabId, shopTabsInfo } from './config';

import css from './styles.module.scss';

interface ShopTabsNavProps {
  /** Tabs with something to collect get a green dot */
  markers?: Partial<Record<ShopTabId, boolean>>;
}

const ShopTabsNav: FC<ShopTabsNavProps> = ({ markers }) => {
  const { t } = useTranslation();
  const { activeTabId, setActiveTabId } = useContext(TabsContext);
  const ref = useHorizontalScroll<HTMLDivElement>();

  // A deep link can open a tab that is out of view (Promo on a narrow phone)
  useEffect(() => {
    const active = ref.current?.querySelector<HTMLElement>(
      `[data-tab="${activeTabId}"]`,
    );
    const list = ref.current;

    if (!active || !list) return;

    const left =
      active.offsetLeft - (list.clientWidth - active.clientWidth) / 2;
    list.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [activeTabId]);

  return (
    <div ref={ref} className={css.list}>
      {shopTabsInfo.map(({ id, label, png, webp, scale }) => {
        const active = id === activeTabId;

        return (
          <button
            key={id}
            type="button"
            data-tab={id}
            className={cn(css.chip, active && css.active)}
            onClick={() => setActiveTabId(id)}
          >
            <div className={css.icon}>
              <Picture
                img={png}
                webp={webp}
                alt=""
                style={{ transform: `scale(${scale})` }}
                className="block size-full"
              />
            </div>
            <span className={css.label}>{t(label)}</span>
            {markers?.[id] && <div className={css.marker} />}
          </button>
        );
      })}
    </div>
  );
};

export { ShopTabsNav };
